import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Config, LoginResponse, Me, PublicConfig, Theme, UserPreferences } from '@mnc/contracts';
import { api, UNAUTHORIZED_EVENT } from './api';
import { applyLocale, type Locale } from '../i18n';

interface AuthState {
  ready: boolean;
  me: Me | null;
  config: Config | null;
  publicConfig: PublicConfig | null;
  login: (identifier: string, password: string, totpCode?: string, recoveryCode?: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setLocale: (l: Locale) => Promise<void>;
  setTheme: (t: Theme) => Promise<void>;
  can: (page: string, level?: 'can_view' | 'can_create' | 'can_edit' | 'can_delete') => boolean;
  canAction: (action: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);
const THEME_KEY = 'mnc.theme';

export function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function storedTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null);

  const loadSession = useCallback(async () => {
    try {
      const m = await api.get<Me>('/api/me');
      setMe(m);
      applyLocale(m.locale);
      applyTheme(m.theme);
      const c = await api.get<Config>('/api/config');
      setConfig(c);
      setPublicConfig(c);
    } catch {
      setMe(null);
      setConfig(null);
      const pc = await api.get<PublicConfig>('/api/config/public').catch(() => null);
      setPublicConfig(pc);
    }
  }, []);

  useEffect(() => {
    applyTheme(storedTheme());
    void loadSession().finally(() => setReady(true));
    const onUnauthorized = () => {
      setMe(null);
      setConfig(null);
      qc.clear();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [loadSession, qc]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      me,
      config,
      publicConfig,
      login: async (identifier, password, totpCode, recoveryCode) => {
        const res = await api.post<LoginResponse>('/api/auth/login', { identifier, password, ...(totpCode ? { totp_code: totpCode } : {}), ...(recoveryCode ? { recovery_code: recoveryCode } : {}) });
        await loadSession();
        return res;
      },
      logout: async () => {
        await api.post('/api/auth/logout').catch(() => undefined);
        setMe(null);
        setConfig(null);
        qc.clear();
      },
      refresh: loadSession,
      setLocale: async (locale) => {
        applyLocale(locale);
        if (me) {
          const p = await api.patch<UserPreferences>('/api/me/preferences', { locale });
          setMe((m) => (m ? { ...m, locale: p.locale } : m));
        }
      },
      setTheme: async (theme) => {
        applyTheme(theme);
        if (me) {
          const p = await api.patch<UserPreferences>('/api/me/preferences', { theme });
          setMe((m) => (m ? { ...m, theme: p.theme } : m));
        }
      },
      can: (page, level = 'can_view') => Boolean(me?.pages.find((p) => p.code === page)?.[level]),
      canAction: (action) => Boolean(me?.actions.includes(action)),
    }),
    [ready, me, config, publicConfig, loadSession, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
